import * as React from 'react';

export interface MenuIconProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
}

/**
 * Icons extracted verbatim from the Noor.Merchant Figma file (Eats menu
 * screens). Each glyph is the exact vector from the design; hardcoded fills and
 * strokes are swapped for `currentColor` so callers control size (`size-*`) and
 * tint. Solid glyphs use a 20×20 viewBox (their native Figma frame); stroked
 * glyphs keep their own frame noted per-icon.
 */

/** Close (X) — the drawer/dialog dismiss glyph (Figma "Close button"). */
export function MenuCloseIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="10 10 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M22.0001 24.122L27.3031 29.425C27.5845 29.7064 27.9661 29.8645 28.3641 29.8645C28.762 29.8645 29.1437 29.7064 29.4251 29.425C29.7065 29.1436 29.8646 28.7619 29.8646 28.364C29.8646 27.966 29.7065 27.5844 29.4251 27.303L24.1201 22L29.4241 16.697C29.5634 16.5577 29.6738 16.3923 29.7492 16.2102C29.8245 16.0282 29.8633 15.8331 29.8632 15.6361C29.8632 15.4391 29.8243 15.2441 29.7489 15.0621C29.6735 14.8801 29.5629 14.7148 29.4236 14.5755C29.2843 14.4362 29.1189 14.3258 28.9368 14.2504C28.7548 14.1751 28.5597 14.1363 28.3627 14.1364C28.1657 14.1364 27.9707 14.1753 27.7887 14.2507C27.6067 14.3261 27.4414 14.4367 27.3021 14.576L22.0001 19.879L16.6971 14.576C16.5588 14.4327 16.3933 14.3183 16.2104 14.2396C16.0274 14.1609 15.8306 14.1194 15.6314 14.1176C15.4322 14.1158 15.2347 14.1537 15.0503 14.229C14.866 14.3043 14.6984 14.4156 14.5575 14.5564C14.4166 14.6972 14.3052 14.8646 14.2296 15.0489C14.1541 15.2332 14.1161 15.4307 14.1177 15.6299C14.1194 15.829 14.1606 16.0259 14.2392 16.2089C14.3177 16.392 14.4319 16.5575 14.5751 16.696L19.8801 22L14.5761 27.304C14.4329 27.4424 14.3187 27.608 14.2402 27.791C14.1616 27.9741 14.1204 28.1709 14.1187 28.3701C14.1171 28.5693 14.1551 28.7668 14.2306 28.9511C14.3062 29.1354 14.4176 29.3028 14.5585 29.4436C14.6994 29.5844 14.867 29.6957 15.0513 29.771C15.2357 29.8463 15.4332 29.8842 15.6324 29.8824C15.8316 29.8805 16.0284 29.8391 16.2114 29.7604C16.3943 29.6817 16.5598 29.5673 16.6981 29.424L22.0001 24.123V24.122Z"
      />
    </svg>
  );
}

/** Package box — the drawer's featured-tile glyph (Figma "box"). */
export function MenuBoxIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="13 15 26 21"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M15.7646 16.9466C15.8822 16.7075 16.1409 16.5703 16.4035 16.6056L25.9983 17.8049L35.5932 16.6056C35.8558 16.5742 36.1145 16.7114 36.2321 16.9466L37.8665 20.2154C38.2192 20.917 37.843 21.7675 37.0904 21.9831L30.686 23.8135C30.1412 23.9703 29.5572 23.739 29.2672 23.253L25.9983 17.8049L22.7295 23.253C22.4395 23.739 21.8554 23.9703 21.3106 23.8135L14.9101 21.9831C14.1537 21.7675 13.7813 20.917 14.1341 20.2154L15.7646 16.9466ZM26.0414 20.3134L28.1932 23.8958C28.7772 24.8678 29.9413 25.3303 31.0349 25.0168L36.0322 23.5901V30.1356C36.0322 30.9979 35.4443 31.7504 34.6055 31.9621L26.6058 33.961C26.2061 34.0629 25.7867 34.0629 25.3908 33.961L17.3912 31.9621C16.5524 31.7465 15.9645 30.994 15.9645 30.1317V23.5862L20.9657 25.0168C22.0553 25.3303 23.2233 24.8678 23.8073 23.8958L25.9552 20.3134H26.0414Z" />
    </svg>
  );
}

/** Plus — the add glyph on buttons and section headers (Figma plus). */
export function MenuPlusIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="25.5 15 18 18"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M35.8846 16.3846C35.8846 15.6187 35.2659 15 34.5 15C33.7341 15 33.1154 15.6187 33.1154 16.3846V22.6154H26.8846C26.1187 22.6154 25.5 23.2341 25.5 24C25.5 24.7659 26.1187 25.3846 26.8846 25.3846H33.1154V31.6154C33.1154 32.3813 33.7341 33 34.5 33C35.2659 33 35.8846 32.3813 35.8846 31.6154V25.3846H42.1154C42.8813 25.3846 43.5 24.7659 43.5 24C43.5 23.2341 42.8813 22.6154 42.1154 22.6154H35.8846V16.3846Z" />
    </svg>
  );
}

/** Chevron-down — trailing glyph on selects and the branch picker (Figma "chevron-down"). */
export function MenuChevronDownIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M5 7.5L9.29289 11.7929C9.68342 12.1834 10.3166 12.1834 10.7071 11.7929L15 7.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Magnifier — the search-field leading glyph (Figma "search"). */
export function MenuSearchIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.2476 8.12381C16.2476 9.91652 15.6657 11.5725 14.6853 12.9161L19.6299 17.8646C20.1181 18.3528 20.1181 19.1456 19.6299 19.6338C19.1417 20.1221 18.3489 20.1221 17.8607 19.6338L12.9161 14.6853C11.5725 15.6696 9.91652 16.2476 8.12381 16.2476C3.63619 16.2476 0 12.6114 0 8.12381C0 3.63619 3.63619 0 8.12381 0C12.6114 0 16.2476 3.63619 16.2476 8.12381ZM8.12381 13.748C8.86239 13.748 9.59373 13.6025 10.2761 13.3199C10.9584 13.0372 11.5784 12.623 12.1007 12.1007C12.623 11.5784 13.0372 10.9584 13.3199 10.2761C13.6025 9.59373 13.748 8.86239 13.748 8.12381C13.748 7.38523 13.6025 6.65389 13.3199 5.97153C13.0372 5.28917 12.623 4.66917 12.1007 4.14692C11.5784 3.62466 10.9584 3.21039 10.2761 2.92775C9.59373 2.64511 8.86239 2.49963 8.12381 2.49963C7.38523 2.49963 6.65389 2.64511 5.97153 2.92775C5.28917 3.21039 4.66917 3.62466 4.14692 4.14692C3.62466 4.66917 3.21039 5.28917 2.92775 5.97153C2.64511 6.65389 2.49963 7.38523 2.49963 8.12381C2.49963 8.86239 2.64511 9.59373 2.92775 10.2761C3.21039 10.9584 3.62466 11.5784 4.14692 12.1007C4.66917 12.623 5.28917 13.0372 5.97153 13.3199C6.65389 13.6025 7.38523 13.748 8.12381 13.748Z"
      />
    </svg>
  );
}

/** Filled map-marker pin — the branch-selector leading glyph (Figma "location"). */
export function MenuLocationIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 1.667c-3.222 0-5.833 2.611-5.833 5.833 0 3.988 4.166 8.75 5.02 9.68a1.1 1.1 0 0 0 1.626 0c.854-.93 5.02-5.692 5.02-9.68 0-3.222-2.611-5.833-5.833-5.833Zm0 8.125a2.292 2.292 0 1 1 0-4.584 2.292 2.292 0 0 1 0 4.584Z"
      />
    </svg>
  );
}

/** Pencil-in-square — the product-card edit glyph (Figma "edit-01"). */
export function MenuEditIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M5.83333 2.5C3.99238 2.5 2.5 3.99238 2.5 5.83333V14.1667C2.5 16.0076 3.99239 17.5 5.83334 17.5H14.1667C16.0076 17.5 17.5 16.0076 17.5 14.1667L17.5 10.8333C17.5 10.3731 17.1269 10 16.6667 10C16.2064 10 15.8333 10.3731 15.8333 10.8333L15.8333 14.1667C15.8333 15.0871 15.0871 15.8333 14.1667 15.8333H5.83334C4.91286 15.8333 4.16667 15.0871 4.16667 14.1667V5.83333C4.16667 4.91286 4.91286 4.16667 5.83333 4.16667H9.16667C9.6269 4.16667 10 3.79357 10 3.33333C10 2.8731 9.6269 2.5 9.16667 2.5H5.83333Z" />
      <path d="M7.91574 9.34325L7.50857 11.7863C7.43944 12.201 7.799 12.5606 8.21374 12.4914L10.6567 12.0843C10.9087 12.0423 11.1412 11.9226 11.3219 11.742L15.4291 7.63471C15.6383 7.4255 15.6383 7.08631 15.4291 6.8771L13.1229 4.57087C12.9137 4.36167 12.5745 4.36166 12.3653 4.57087L8.25801 8.67814C8.0774 8.85876 7.95773 9.0913 7.91574 9.34325Z" />
      <path d="M16.8655 3.13454C17.6005 3.8696 17.697 5.00145 17.1548 5.84063C17.07 5.97179 16.8874 5.97834 16.777 5.86792L14.1321 3.22304C14.0217 3.11262 14.0282 2.92998 14.1594 2.84524C14.9985 2.30305 16.1304 2.39948 16.8655 3.13454Z" />
    </svg>
  );
}

/** Three vertical dots — the category-header menu glyph (Figma "dots-vertical"). */
export function MenuDotsVerticalIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14 10C14 8.89543 14.8954 8 16 8C17.1046 8 18 8.89543 18 10C18 11.1046 17.1046 12 16 12C14.8954 12 14 11.1046 14 10ZM14 16C14 14.8954 14.8954 14 16 14C17.1046 14 18 14.8954 18 16C18 17.1046 17.1046 18 16 18C14.8954 18 14 17.1046 14 16ZM14 22C14 20.8954 14.8954 20 16 20C17.1046 20 18 20.8954 18 22C18 23.1046 17.1046 24 16 24C14.8954 24 14 23.1046 14 22Z"
      />
    </svg>
  );
}

/**
 * Trash (outline) — the destructive "delete" glyph in action menus (Figma
 * "Huge-icon/interface/outline/trash 01"). Stroked, so it tints via
 * `currentColor` — action menus render it in the destructive/red tone.
 */
export function MenuTrashIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="12 12 20 20"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M27.8333 19.5001L27.2367 27.2557C27.1032 28.9924 25.655 30.3334 23.9132 30.3334H20.0868C18.345 30.3334 16.8968 28.9924 16.7633 27.2557L16.1667 19.5001M29.5 17.8334C27.3351 16.7784 24.7614 16.1667 22 16.1667C19.2386 16.1667 16.6649 16.7784 14.5 17.8334M20.3333 16.1667V15.3334C20.3333 14.4129 21.0795 13.6667 22 13.6667C22.9205 13.6667 23.6667 14.4129 23.6667 15.3334V16.1667M20.3333 21.1667V26.1667M23.6667 21.1667V26.1667"
        stroke="currentColor"
        strokeWidth="1.67"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Solid trash — the branch-card delete glyph from Figma node 1338:6626. */
export function MenuTrashSolidIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 16 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M15 5V16C15 18.2091 13.2091 20 11 20H5C2.79086 20 1 18.2091 1 16V5H15ZM6 8.25C5.58579 8.25 5.25 8.58579 5.25 9V16C5.25 16.4142 5.58579 16.75 6 16.75C6.41421 16.75 6.75 16.4142 6.75 16V9C6.75 8.58579 6.41421 8.25 6 8.25ZM10 8.25C9.58579 8.25 9.25 8.58579 9.25 9V16C9.25 16.4142 9.58579 16.75 10 16.75C10.4142 16.75 10.75 16.4142 10.75 16V9C10.75 8.58579 10.4142 8.25 10 8.25ZM8.92969 0C9.59837 0.0000184288 10.2228 0.334243 10.5938 0.890625L11.5 2.25H15.25C15.6642 2.25 16 2.58579 16 3C16 3.41421 15.6642 3.75 15.25 3.75H0.75C0.335786 3.75 0 3.41421 0 3C0 2.58579 0.335786 2.25 0.75 2.25H4.5L5.40625 0.890625C5.77717 0.334243 6.40163 0.0000184288 7.07031 0H8.92969Z" />
    </svg>
  );
}

/** Calendar — the date-range pill leading glyph (Figma "calendar"). */
export function MenuCalendarIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M3.88867 1L3.88867 2L2.38867 2C1.56055 2 0.888672 2.67188 0.888672 3.5L0.888673 5L14.8887 5L14.8887 3.5C14.8887 2.67188 14.2168 2 13.3887 2L11.8887 2L11.8887 1C11.8887 0.446876 11.4418 9.52362e-07 10.8887 1.04907e-06C10.3355 1.14578e-06 9.88867 0.446876 9.88867 1L9.88867 2L5.88867 2L5.88867 1C5.88867 0.446877 5.4418 2.00144e-06 4.88867 2.09815e-06C4.33555 2.19486e-06 3.88867 0.446877 3.88867 1ZM14.8887 6L0.888673 6L0.888674 14.5C0.888675 15.3281 1.56055 16 2.38867 16L13.3887 16C14.2168 16 14.8887 15.3281 14.8887 14.5L14.8887 6Z" />
    </svg>
  );
}

/**
 * Pencil (outline, "edit-03") — the table-row edit action glyph. Stroked, so it
 * tints via `currentColor`; the users table renders it in the brand tone
 * (`text-primary`).
 */
export function MenuEdit03Icon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M10.35 14.6637H14.85M3.15002 14.6637L6.42451 14.0039C6.59834 13.9689 6.75796 13.8833 6.88331 13.7579L14.2136 6.42358C14.565 6.07194 14.5648 5.50195 14.213 5.1506L12.6602 3.59955C12.3086 3.24835 11.7389 3.24859 11.3876 3.60008L4.0566 10.9351C3.93149 11.0603 3.84606 11.2196 3.811 11.3931L3.15002 14.6637Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Circle with an X (outline, "x-circle") — the table-row delete action glyph.
 * Stroked, so it tints via `currentColor`; the users table renders it in the
 * destructive tone (`text-destructive`).
 */
export function MenuXCircleIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M7.5 10.5L9 9M9 9L10.5 7.5M9 9L7.5 7.5M9 9L10.5 10.5M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z"
        stroke="currentColor"
        strokeWidth="1.125"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Package box (solid, "package box 03") — the branch-sales "Топ товар" leading
 * glyph. Solid fill with the two shelf-lines and the lid seam carved out, so it
 * renders with `fillRule="evenodd"` and tints via `currentColor`.
 */
export function MenuPackageBox03Icon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19.9453 5.75C19.9809 5.9648 20 6.18328 20 6.40332V16C20 18.2091 18.2091 20 16 20H4C1.79086 20 0 18.2091 0 16V6.40332C0 6.18328 0.0191273 5.9648 0.0546875 5.75H19.9453ZM3 10.25C2.58579 10.25 2.25 10.5858 2.25 11C2.25 11.4142 2.58579 11.75 3 11.75H6C6.41421 11.75 6.75 11.4142 6.75 11C6.75 10.5858 6.41421 10.25 6 10.25H3ZM3 7.25C2.58579 7.25 2.25 7.58579 2.25 8C2.25 8.41421 2.58579 8.75 3 8.75H6C6.41421 8.75 6.75 8.41421 6.75 8C6.75 7.58579 6.41421 7.25 6 7.25H3ZM14.0771 0C15.2923 0 16.4421 0.552148 17.2012 1.50098L19.123 3.9043C19.2117 4.01507 19.2923 4.13139 19.3682 4.25H0.631836C0.707658 4.13139 0.788339 4.01507 0.876953 3.9043L2.79883 1.50098C3.55792 0.552148 4.70774 0 5.92285 0H14.0771Z"
      />
    </svg>
  );
}

/**
 * Photo-with-a-plus ("image-add-line") — the empty state of the branch modal's
 * dashed image uploaders (Figma 790:11000). Drawn as a single outline path, so
 * it tints via `currentColor` like the rest of this set.
 */
export function MenuImageAddIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 22 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M19 12V15H22V17H19V20H17V17H14V15H17V12H19ZM19.008 0C19.556 0 20 0.445 20 0.993V10H18V2H2V15.999L12 6L15 9V11.829L12 8.829L4.827 16H12V18H0.992C0.728813 17.9997 0.476497 17.895 0.290489 17.7088C0.104482 17.5226 -1.33455e-07 17.2702 0 17.007V0.993C0.00183004 0.730378 0.1069 0.479017 0.292513 0.293218C0.478126 0.107418 0.72938 0.00209465 0.992 0H19.008ZM6 4C6.53043 4 7.03914 4.21071 7.41421 4.58579C7.78929 4.96086 8 5.46957 8 6C8 6.53043 7.78929 7.03914 7.41421 7.41421C7.03914 7.78929 6.53043 8 6 8C5.46957 8 4.96086 7.78929 4.58579 7.41421C4.21071 7.03914 4 6.53043 4 6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4V4Z" />
    </svg>
  );
}

/**
 * Padlock (outline) — the "change password" glyph in the profile card's action
 * menu (Figma 152:4733, "Interface, Essential/Lock").
 *
 * Stroked rather than solid, on a 24×24 frame (its native Figma size), so it
 * tints through `currentColor` like `MenuTrashIcon`. `vectorEffect` keeps the
 * 1.5px stroke from thickening when the action menu scales it to 20px.
 */
export function MenuLockIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M8 10V7C8 4.791 9.791 3 12 3C14.209 3 16 4.791 16 7V10" />
      <path d="M12 14V17" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17 21H7C5.895 21 5 20.105 5 19V12C5 10.895 5.895 10 7 10H17C18.105 10 19 10.895 19 12V19C19 20.105 18.105 21 17 21Z"
      />
    </svg>
  );
}

/** Trash bin (solid) — bulk delete action glyph from Figma node 1627:37048. */
export function MenuDeleteBinIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M17 4H22V6H20V21C20 21.2652 19.8946 21.5196 19.7071 21.7071C19.5196 21.8946 19.2652 22 19 22H5C4.73478 22 4.48043 21.8946 4.29289 21.7071C4.10536 21.5196 4 21.2652 4 21V6H2V4H7V2H17V4ZM9 9V17H11V9H9ZM13 9V17H15V9H13Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Paper plane (solid) — bulk submit for review action glyph from Figma node 1627:37059. */
export function MenuSendPlaneIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M1.946 9.315C1.424 9.141 1.419 8.86 1.956 8.681L21.043 2.319C21.572 2.143 21.875 2.439 21.727 2.957L16.273 22.043C16.123 22.572 15.818 22.59 15.594 22.088L12 14L18 6L10 12L1.946 9.315V9.315Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Close cross (line) — cancel selection action glyph from Figma node 1627:37064. */
export function MenuCloseLineIcon({ className, ...props }: MenuIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M12 10.5857L16.95 5.63574L18.364 7.04974L13.414 11.9997L18.364 16.9497L16.95 18.3637L12 13.4137L7.04999 18.3637L5.63599 16.9497L10.586 11.9997L5.63599 7.04974L7.04999 5.63574L12 10.5857Z"
        fill="currentColor"
      />
    </svg>
  );
}
